// Source: NASAA "Uniform Investment Adviser Law Examination (Series 65) Overview" (September 1, 2023)
// https://www.nasaa.org/wp-content/uploads/2023/09/NASAA-Series-65-Exam-Study-Guide.pdf

export const examWeighting = [
  { subject: 'Economic Factors and Business Information', weight: 15, questions: 20 },
  { subject: 'Investment Vehicle Characteristics', weight: 25, questions: 32 },
  { subject: 'Client Investment Recommendations and Strategies', weight: 30, questions: 39 },
  { subject: 'Laws, Regulations and Guidelines Including Prohibition on Unethical Business Practices', weight: 30, questions: 39 },
];

export const sourceMaterials = {
  general: 'Finance, Economics and Investing — commonly accepted principles in these fields, drawn from widely taught sources. NASAA does not endorse any particular textbook or prep provider.',
  federalLaw: [
    'Investment Advisers Act of 1940 ("IAA")',
    'Securities Exchange Act of 1934 ("SEA")',
    'Securities Act of 1933 ("SA")',
    'Securities and Exchange Commission ("SEC") rules and regulations',
    'Financial Industry Regulatory Authority ("FINRA") rules',
  ],
  stateLaw: [
    'Uniform Securities Act of 1956, as amended by NASAA ("USA")',
    'NASAA Model Rules, Statements of Policy and Model Acts',
    'Uniform Prudent Investor Act',
  ],
};

export const examStructure = [
  'The exam consists of 140 multiple-choice questions; 130 are scored and 10 are unscored pre-test questions (you won’t know which is which).',
  'You are allowed 180 minutes to complete the exam.',
  'To pass, you must correctly answer at least 92 of the 130 scored questions (a ~70.8% passing threshold).',
  'It is a closed-book exam administered by FINRA. Passing does not by itself confer the right to transact business in any state — state-specific registration is still required.',
  'If you fail, you must wait 30 days before a 2nd attempt, 30 days before a 3rd attempt, and 180 days before a 4th (and each subsequent) attempt.',
];

export const backgroundNote =
  'The Series 65 exam is developed by the North American Securities Administrators Association ("NASAA"), a non-profit association of state, provincial, and territorial securities regulators across the U.S., Canada, and Mexico. Passing the exam (or holding certain qualifying professional designations) is normally a prerequisite for registration as an investment adviser representative in a state. Exam questions may test the underlying concept without naming the specific statute or model rule — you are expected to recognize the principle being tested even when it isn’t labeled.';

// Full breakdown of the four subject-matter areas and their tested components,
// reproduced from NASAA's official test specifications.
export const subjectMatters = [
  {
    id: 'economic-factors',
    title: '2.1 Economic Factors and Business Information',
    weight: '15% — 20 questions',
    components: [
      {
        heading: 'Basic Economic Concepts',
        items: [
          'Business cycles',
          'Monetary and fiscal policies',
          'Global and geopolitical factors (e.g., currency valuation, effects of exchange rates on investment returns, sovereign debt)',
          'Inflation/deflation',
          'Interest rates, yield curves, credit spreads',
          'Economic indicators (e.g., gross domestic product, employment indicators, trade deficit, balance of payments, consumer price index)',
        ],
      },
      {
        heading: 'Financial Reporting',
        items: [
          'Financial reports (e.g., income statement, balance sheet, statement of cash flows, auditor disclosures, corporate SEC filings, annual reports)',
          'Accounting fundamentals (e.g., audited vs. unaudited financials, cash vs. accrual accounting)',
        ],
      },
      {
        heading: 'Analytical Methods',
        items: [
          'Time value of money concepts (e.g., internal rate of return, net present value)',
          'Descriptive statistics (e.g., mean, median, mode, ranges, standard deviation, Alpha, Beta, Sharpe ratios, correlations)',
          'Financial ratios and their uses (e.g., current ratio, quick ratio, debt-to-equity ratio)',
          'Valuation ratios (e.g., price-to-earnings ratio, price-to-book ratio)',
        ],
      },
      {
        heading: 'Types of Risk',
        items: ['Systematic risks', 'Unsystematic risks', 'Opportunity cost', 'Capital structure including liquidation priority'],
      },
    ],
  },
  {
    id: 'investment-vehicles',
    title: '2.2 Investment Vehicle Characteristics',
    weight: '25% — 32 questions',
    components: [
      {
        heading: 'Types and Characteristics of Cash and Cash Equivalents',
        items: ['Insured deposits (e.g., demand deposits, certificates of deposit)', 'Money market instruments (e.g., commercial paper, Treasury bills)'],
      },
      {
        heading: 'Types of Fixed Income Securities',
        items: [
          'U.S. government and agency securities (e.g., Treasury securities, mortgage-backed securities from Fannie Mae or Freddie Mac, TIPS)',
          'Asset-backed securities',
          'Corporate bonds',
          'Municipal bonds (e.g., general obligation, revenue, insured vs. uninsured)',
          'Foreign bonds (e.g., government debt, corporate debt)',
        ],
      },
      {
        heading: 'Characteristics and Valuation Factors of Fixed Income Securities',
        items: [
          'Characteristics (e.g., tax implications, market liquidity, liquidation preference, call features, coupon vs. zero coupon, duration, premium)',
          'Valuation factors (e.g., maturity, yield-to-call, yield-to-maturity, conversion valuation, bond ratings, credit spread, discounted cash flow)',
        ],
      },
      { heading: 'Types of Equity Securities', items: ['Common stock, domestic and foreign', 'Preferred and convertible preferred stock'] },
      {
        heading: 'Characteristics of Equity Securities',
        items: [
          'Shareholder rights (e.g., voting rights, antidilution, liquidation preferences)',
          'Restricted stock and resale restrictions',
          'Dividends',
          'Employee stock options (e.g., incentive, nonqualified)',
        ],
      },
      {
        heading: 'Methods Used to Determine the Value of Equity Securities',
        items: ['Technical analysis', 'Fundamental analysis', 'Dividend discount', 'Discounted cash flow'],
      },
      { heading: 'Equity Public Offering', items: ['Initial public offering (IPO)', 'Secondary offering', 'SPAC/blind pools/blank check'] },
      {
        heading: 'Types of Pooled Investments',
        items: [
          'Mutual funds (open-end vs. closed-end)',
          'Private funds (e.g., hedge funds, private equity funds, venture capital funds)',
          'Unit investment trusts ("UITs")',
          'Exchange-traded funds ("ETFs")',
          'Real estate investment trusts ("REITs"), including exchange-listed and non-traded REITs',
        ],
      },
      {
        heading: 'Characteristics of Pooled Investments',
        items: [
          'Share classes',
          'Liquidity',
          'Tax implications',
          'Fee structures and other costs (e.g., load vs. no-load funds, contingent deferred sales charges, Rule 12b-1 fees, breakpoints)',
          'Pricing (e.g., net asset value ("NAV"), discount/premium)',
          'Benefits and risks',
          'Relative comparisons (e.g., benchmarks, manager tenure, securities indexes)',
        ],
      },
      { heading: 'Types of Derivative Securities', items: ['Options and warrants (definitions)', 'Futures and forward contracts (definitions)'] },
      { heading: 'Characteristics of Derivative Securities', items: ['Costs, benefits, and risks of derivative securities'] },
      {
        heading: 'Alternative Investments',
        items: ['Limited partnerships', 'Exchange traded notes ("ETNs")', 'Leveraged funds', 'Inverse funds', 'Structured products'],
      },
      { heading: 'Insurance Based Products', items: ['Annuities (e.g., fixed, variable, equity indexed)', 'Life insurance (e.g., whole, term, universal, variable)'] },
      { heading: 'Other Assets', items: ['Commodities and precious metals', 'Digital assets'] },
    ],
  },
  {
    id: 'client-recommendations',
    title: '2.3 Client Investment Recommendations and Strategies',
    weight: '30% — 39 questions',
    components: [
      {
        heading: 'Type of Client',
        items: [
          'Individual, natural person, sole proprietorship',
          'Business entities (e.g., general partnership, limited partnership, limited liability company, C and S corporations)',
          'Trusts and estates',
          'Foundations and charities',
        ],
      },
      {
        heading: 'Client Profile',
        items: [
          'Financial goals and objectives',
          'Current and future financial situation (e.g., cash flow, balance sheet, existing investments, tax situation, social security and pensions)',
          'Risk tolerance',
          'Nonfinancial investment considerations (e.g., values, attitudes, experience, demographics, life events, behavioral finance)',
          'Client data gathering (e.g., client identification, questionnaires, interviews)',
          'Time horizon',
        ],
      },
      {
        heading: 'Capital Market Theory',
        items: ['Investment theories, models, and hypotheses (e.g., capital asset pricing model, modern portfolio theory, efficient market hypothesis)'],
      },
      {
        heading: 'Portfolio Management Strategies, Styles and Techniques',
        items: [
          'Strategies (e.g., strategic asset allocation, tactical asset allocation)',
          'Styles (e.g., active, passive, growth, value, income, capital appreciation)',
          'Techniques (e.g., diversification, sector rotation, dollar-cost averaging, puts, calls, leveraging, volatility management, inverse strategies, high frequency trading)',
        ],
      },
      {
        heading: 'Tax Considerations',
        items: [
          'Income tax fundamentals: individual (e.g., capital gains, qualified dividends, tax basis, marginal tax bracket, alternative minimum tax)',
          'Income tax fundamentals: corporations, trusts and estates',
          'Wealth transfer, estate tax and gift tax fundamentals',
        ],
      },
      {
        heading: 'Retirement Plans',
        items: ['Individual retirement accounts (e.g., traditional, Roth)', 'Solo 401(k) (e.g., traditional, Roth)', 'Qualified retirement plans', 'Nonqualified retirement plans'],
      },
      {
        heading: 'Employee Retirement Income Security Act (ERISA) Issues',
        items: ['Fiduciary issues (e.g., investment choices, ERISA § 404(c))', 'Investment policy statement', 'Prohibited transactions'],
      },
      {
        heading: 'Special Types of Accounts',
        items: ['Education-related (e.g., 529 plans, Coverdell accounts)', 'Uniform Transfers to Minors Act (UTMA) and Uniform Gifts to Minors Act (UGMA)', 'Health savings accounts (HSA)'],
      },
      {
        heading: 'Ownership and Estate Planning Techniques',
        items: [
          'Types of ownership (e.g., joint tenants with rights of survivorship, tenants in common, tenancy by the entirety)',
          'Pay on death and transfer on death',
          'Beneficiary designation',
          'Trusts and wills',
          'Qualified domestic relations order',
          'Donor advised funds',
        ],
      },
      {
        heading: 'Trading Securities',
        items: [
          'Terminology (e.g., bids, offers, quotes, market orders, limit or stop orders, short sales, cash accounts, margin accounts, principal or agency trades, payment for order flow)',
          'Roles of broker-dealers, custodians, market makers, and exchanges',
          'Costs of trading securities (e.g., commissions, markups, bid/ask spread, best execution)',
        ],
      },
      {
        heading: 'Portfolio Performance Measures',
        items: [
          'Returns (e.g., risk-adjusted, time-weighted, dollar-weighted, annualized, total, holding period, internal rate of return, expected, inflation-adjusted, after tax)',
          'Current yield',
          'Relevant benchmarks',
        ],
      },
    ],
  },
  {
    id: 'laws-regulations',
    title: '2.4 Laws, Regulations and Guidelines Including Prohibition on Unethical Business Practices',
    weight: '30% — 39 questions',
    components: [
      {
        heading: 'Regulation of Investment Advisers, Including State-Registered Advisers and Federal Covered Advisers',
        items: [
          'Definitions of an investment adviser (USA § 401, IAA § 202)',
          'Notice filing requirements',
          'Registration and post-registration requirements (e.g., required books and records, Form ADV, IAA §§ 203/203A, USA §§ 201–204, NASAA Recordkeeping/Brochure/Financial Reporting Model Rules)',
          'Exemptions for exempt reporting advisers and private fund advisers',
          'Investment adviser representative supervision',
        ],
      },
      {
        heading: 'Regulation of Investment Adviser Representatives',
        items: ['Definition of an investment adviser representative (USA § 401)', 'Registration and post-registration requirements (USA §§ 201–204, Form U4)'],
      },
      {
        heading: 'Regulation of Broker-Dealers',
        items: ['Definition of a broker-dealer (USA § 401, IAA § 202)', 'Definition of an underwriter, market maker, associated person', 'Registration and post-registration requirements'],
      },
      { heading: 'Regulation of Broker-Dealer Agents', items: ['Definition of a broker-dealer agent (USA § 401)', 'Registration and post-registration requirements (e.g., Form U4)'] },
      {
        heading: 'Regulation of Securities and Issuers',
        items: [
          'Definitions of security and issuer (USA § 401)',
          'Securities registration process, including federal/state registration, notice filing, exemptions, and post-registration requirements (USA §§ 301–305, 402)',
          'State antifraud authority',
        ],
      },
      {
        heading: 'Remedies and Administrative Provisions',
        items: ['Authority of state securities administrator (USA §§ 204, 306, 406, 407, 412)', 'Administrative actions (USA § 408)', 'Other penalties and liabilities (USA §§ 409–411)'],
      },
      {
        heading: 'Communication with Clients and Prospects',
        items: ['Disclosures', 'Unlawful representations concerning registrations', 'Performance guarantees', 'Client contracts (USA § 102, IAA § 205)', 'Correspondence and advertising (USA §§ 403, 405)'],
      },
      {
        heading: 'Ethical Practices and Fiduciary Obligations',
        items: [
          'Compensation (e.g., fees, commissions, performance-based fees, "pay to play," soft dollars)',
          'Client funds and securities (e.g., custody, trading discretion, prudent investor standards, suitability, anti-money laundering)',
          'Custody conditions and obligations',
          'Conflicts of interest, criminal activities, fiduciary and other ethical considerations (e.g., loans to/from clients, client confidentiality, insider trading, market manipulation, personal securities transactions)',
          'Cybersecurity, privacy, and data protection',
          'Business continuity plans (e.g., disaster recovery, succession planning)',
        ],
      },
    ],
  },
];
